/**
 * Cliente para la API de front (Laravel) en tiempo real.
 * Base URL: BASE_FRONT (ej. https://dashboard.dondelanegra.cl o http://localhost:8001)
 * Todas las rutas están bajo: ${BASE_FRONT}/api/front/...
 *
 * Incluye cache en memoria con TTL y deduplicación de requests concurrentes.
 */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutos
const cache = new Map();
const inflight = new Map();

function getCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(key, data) {
  cache.set(key, { data, ts: Date.now() });
}

const getBaseUrl = () => {
  const base = process.env.BASE_FRONT || '';
  if (!base) {
    console.warn('BASE_FRONT no está definido; las llamadas a /api/front pueden fallar.');
  }
  return base.replace(/\/$/, '');
};

const apiFrontBase = () => `${getBaseUrl().replace(/\/$/, '')}/api/front`;

/**
 * GET genérico a la API front con cache y deduplicación.
 * @param {string} path - Ruta relativa a /api/front (ej. "home", "products/slug")
 * @param {Record<string, string|number>} [params] - Query params
 * @returns {Promise<any>}
 */
export async function apiFrontGet(path, params = {}) {
  const base = apiFrontBase();
  const url = new URL(`${base}/${path.replace(/^\//, '')}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  const cacheKey = url.toString();

  // Retornar desde cache si disponible
  const cached = getCached(cacheKey);
  if (cached) return cached;

  // Deduplicar requests concurrentes al mismo URL
  if (inflight.has(cacheKey)) {
    return inflight.get(cacheKey);
  }

  const promise = fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
    .then(async (res) => {
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API front ${path}: ${res.status} ${text || res.statusText}`);
      }
      const data = await res.json();
      setCache(cacheKey, data);
      return data;
    })
    .finally(() => {
      inflight.delete(cacheKey);
    });

  inflight.set(cacheKey, promise);
  return promise;
}

// --- Home
/**
 * Datos del home: últimos productos, ofertas flash, recomendados (perfect gift).
 * @param {number} [limit=12] - Máximo 48
 */
export async function getHome(limit = 12) {
  return apiFrontGet('home', { limit });
}

// --- Producto
/** Detalle de un producto por slug. */
export async function getProduct(slug) {
  return apiFrontGet(`products/${encodeURIComponent(slug)}`);
}

// --- Buscador
/**
 * Búsqueda paginada por texto (nombre, SKU, descripción).
 * Para términos cortos (<=4 caracteres), envía exact=1 para solicitar coincidencia exacta al backend.
 * @param {string} q - Término de búsqueda
 * @param {number} [page=1]
 * @param {number} [per_page=24] - Máximo 48
 */
export async function search(q, page = 1, per_page = 24) {
  const params = { q, page, per_page };
  // Para términos cortos, indicar al backend que busque coincidencia exacta
  if (q.trim().length <= 4) {
    params.exact = 1;
  }
  return apiFrontGet('search', params);
}

// --- Blog / Novedades (datos en vivo)
/**
 * Reescribe en el HTML del blog las rutas de /storage relativas
 * (../../storage/... o /storage/...) a URLs absolutas del backend usando
 * BASE_FRONT, porque el contenido se guardó con rutas relativas y al
 * renderizarse en el frontend apuntarían a este dominio en vez del backend.
 * @param {string} html
 * @returns {string}
 */
export function absolutizeStorageHtml(html) {
  if (!html) return html;
  const base = getBaseUrl(); // p.ej. https://staging.dondelanegra.cl
  if (!base) return html;
  return html
    // src/href con cualquier cantidad de ../ antes de storage/
    .replace(/((?:src|href)\s*=\s*["'])(?:\.\.\/)+storage\//gi, `$1${base}/storage/`)
    // src/href que apuntan a /storage/ desde la raíz
    .replace(/((?:src|href)\s*=\s*["'])\/storage\//gi, `$1${base}/storage/`);
}

/** Listado paginado de blogs publicados. */
export async function getBlogs(page = 1, per_page = 12) {
  return apiFrontGet('blogs', { page, per_page });
}

/** Un blog publicado por slug. */
export async function getBlog(slug) {
  return apiFrontGet(`blogs/${slug}`);
}

// --- Categorías
/** Todas las categorías con productos (activas, con subcategorías e imágenes). */
export async function getCategories() {
  return apiFrontGet('categories');
}

/** Una categoría por slug con sus productos. */
export async function getCategory(slug) {
  return apiFrontGet(`categories/${encodeURIComponent(slug)}`);
}

// --- Marcas
/** Todas las marcas con productos. */
export async function getBrands() {
  return apiFrontGet('brands');
}

/** Una marca por slug con sus productos. */
export async function getBrand(slug) {
  return apiFrontGet(`brands/${encodeURIComponent(slug)}`);
}

// --- Etiquetas (tags)
/** Todas las etiquetas con productos. */
export async function getTags() {
  return apiFrontGet('tags');
}

/** Una etiqueta por slug con sus productos. */
export async function getTag(slug) {
  return apiFrontGet(`tags/${encodeURIComponent(slug)}`);
}

// --- Distribuidores (suppliers)
/** Todos los distribuidores con productos. */
export async function getSuppliers() {
  return apiFrontGet('suppliers');
}

/** Un distribuidor por slug con sus productos. */
export async function getSupplier(slug) {
  return apiFrontGet(`suppliers/${encodeURIComponent(slug)}`);
}

// --- Envío el mismo día (tipo MELI)
/**
 * Obtiene la información de envío el mismo día para el usuario logueado,
 * según la comuna de su dirección principal.
 * Usa el endpoint plano de la API (BASE_URL ya incluye /api), no /api/front.
 * @param {number|string} userId
 * @returns {Promise<{show_message:boolean, message?:string, cutoff_time?:string, delivery_type?:string, estimated_date?:string}>}
 */
export async function getSameDayShippingInfo(userId) {
  if (!userId) return { show_message: false };
  try {
    const base = (process.env.BASE_URL || '').replace(/\/$/, '');
    const res = await fetch(
      `${base}/shipping/same-day-info?user_id=${encodeURIComponent(userId)}`,
      { method: 'GET', headers: { Accept: 'application/json' } }
    );
    if (!res.ok) return { show_message: false };
    const data = await res.json();
    return data && typeof data.show_message === 'boolean' ? data : { show_message: false };
  } catch (e) {
    return { show_message: false };
  }
}

// --- Páginas administrables (about, etc.)
/** Una página publicada por slug. */
export async function getPage(slug) {
  return apiFrontGet(`pages/${encodeURIComponent(slug)}`);
}

// --- Drivin tracking
/**
 * Obtiene el estado de tracking Drivin para una orden del usuario autenticado.
 * @param {number|string} orderId
 * @returns {Promise<any>}
 */
export async function getDrivinTracking(orderId) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const url = `${getBaseUrl()}/api/orders/${orderId}/drivin-tracking`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export default {
  getBaseUrl,
  apiFrontBase,
  apiFrontGet,
  getHome,
  getProduct,
  search,
  getCategories,
  getCategory,
  getBrands,
  getBrand,
  getTags,
  getTag,
  getSuppliers,
  getSupplier,
  getSameDayShippingInfo,
  getDrivinTracking,
};
